import {Injectable, Optional} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {appData, compareIds, formatAccountDetails, isNullOrUndefOrEmpty, isSubscribersMode, userTemplate} from 'app/common';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {EnumLoginStatus, EnumLoginViewMode, EnumStudioType, IAccountReplyV2, IAccountRequest, IAppStatic, IUser, IUserSync, serverCallType} from "../interfaces";
import {actionAccountCommandsRequest, actionAccountSocialLoginReply, actionAccountSocialLoginRequest, actionLoginViewMode, actionPreferenceUpdateStore, actionSelectedUserUpdate, actionPreferencesGet, actionUpdateAccountDetails} from "../store/actions/user.actions";
import {StoreSelectorsService} from "./store-selectors.service";
import {LoginForm} from "../models";
import {timeout} from "../decorators/timeout";
import {SocialAuthService, SocialUser} from "@abacritt/angularx-social-login";
import {ToastrService} from "ngx-toastr";
import {LocalStoreService} from "./local-store.service";
import {TranslocoService} from "@ngneat/transloco";
import {IGroupsPrivileges} from "../interfaces/enterprise-interface";
import {filter, take} from 'rxjs/operators';
import {IAmazonLogin, ISocialServerReply} from "../interfaces/social-interface";
import {Lib} from "../../libs/Lib";
import * as _ from 'lodash';
import {Compbaser} from "../components/compbaser";

const {localStoreConfigVersion: localStoreConfigVersion} = require('../../../package.json');

@Injectable()
export class AuthUserService extends Compbaser {

    readonly user$: BehaviorSubject<IUser | null> = new BehaviorSubject(null);
    redirectUrl: string | null = null;
    currentUrl: string;
    resellerId: any;
    appSettings: IAppStatic;
    loadLocalStorage = true;

    constructor(
        private translocoService: TranslocoService,
        private router: Router,
        private toastr: ToastrService,
        private localStorage: LocalStoreService,
        private storeSelector: StoreSelectorsService,
        @Optional() private authService: SocialAuthService) {

        super();

        /** when loading in standalone subscriber mode we ignore local storage as all data will come from token / network **/
        if (isSubscribersMode()) {
            this.loadLocalStorage = false;
        }

        this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.INIT}));
        this.rewind();
        this.cod(
            this.router.events
                .pipe(
                    filter(event => event instanceof NavigationEnd)
                ).subscribe((event: NavigationEnd) => {
                    this.currentUrl = event.url;
                }
            )
        );
        this.cod(
            storeSelector.getAppSettings()
                .subscribe((appSettings: IAppStatic) => {
                    this.appSettings = appSettings;
                })
        );
        this.cod(
            this.storeSelector.getResellerId(this.localStorage)
                .subscribe((i_resellerId: number) => {
                    this.resellerId = i_resellerId;
                })
        );
    }

    getSocialAuthState$(): Observable<SocialUser> {
        if (this.authService && this.authService?.authState) return this.authService.authState;
        return of(); // debug local mode
    }

    getSocialAuthService(): SocialAuthService {
        if (this.authService) return this.authService;
    }

    socialSignOut(): void {
        this.storeSelector.dispatch(actionAccountSocialLoginRequest({payload: null}));
        this.storeSelector.dispatch(actionAccountSocialLoginReply({payload: null}));
        if (!this.authService) return console.log('Social login not loaded');
        this.authService.authState.pipe(take(1)).subscribe((v) => {
            if (v) {
                this.authService.signOut().catch().finally();
            }
        });
    }

    private setUserIntoStorage(model: IUser): void {
        this.localStorage.setItem('IUser', model);
    }

    private getUserFromStorage(): IUser | null {
        const user: IUser = this.localStorage.getItem('IUser', null);
        if (!user || user?.localStoreConfigVersion !== localStoreConfigVersion || user?.username.length < 3 || user?.passwordNew.length < 3) {
            this.removeUserFromStorage();
            this.dispatchUser(null);
            this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.LOGIN}));
            return null;
        } else {
            /** if in dev mode we re-load the same router path as before for easy development **/
            if (Lib.DevMode()) {

                // test only updating accountStatus after login
                // user.accountStatus = 4;
                // user.accountStatusReseller = 4;
                // setTimeout(() => {
                //     this.appService.syncUser();
                // }, 5000);

                this.dispatchUser(user);
                return user;
            }
            const m: LoginForm = {username: user.username, password: user.passwordNew};
            this.login(m, true);
            this.dispatchUser(null);
            this.removeUserFromStorage();
            this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.LOGIN_IN_PROGRESS}));
            return null;
        }
    }

    public removeUserFromStorage(): void {
        this.localStorage.removeUserStorage();
    }

    private removeSettingsFromStorage(): void {
        this.localStorage.removeSettingsFromStorage();
    }

    private removeAppCache(): void {
        this.localStorage.unCacheApp();
    }

    public login(model: LoginForm, i_rememberMe: boolean): void {
        if (model.username === '') {
            this.logout();
        } else {
            this.loginInProgress(model, i_rememberMe, 'VERIFY_V2');
        }
    }

    @timeout(1000)
    public loginInProgress(model: LoginForm, rememberMe: boolean, i_serverCallType: serverCallType) {
        this.storeSelector.appStore.dispatch(actionAccountCommandsRequest({
                accountRequest: {
                    verifyAccount: {
                        userName: model.username,
                        password: model.password,
                        rememberMe
                    },
                    serverCall: i_serverCallType
                }
            })
        );
    }

    protected updateUserSharedFields(i_serverReply: IAccountReplyV2, i_userTemplate: IUser): IUser {
        const userTemplateCopy = _.cloneDeep(i_userTemplate);
        userTemplateCopy.accountStatus = Lib.GetAccountStatus(i_serverReply);
        userTemplateCopy.name = i_serverReply.businessUserInfo?.name;
        userTemplateCopy.accountStatusReseller = i_serverReply.businessUserInfo?.resellerInfo?.accountStatus;
        userTemplateCopy.dashboard = !i_serverReply.businessUserInfo.businessInfo;
        userTemplateCopy.customStudio = i_serverReply.businessUserInfo?.resellerInfo?.customStudio;
        userTemplateCopy.webToken = i_serverReply.businessUserInfo?.businessInfo?.webToken;
        userTemplateCopy.loginStatus = EnumLoginStatus.LOGIN_PASSED;
        userTemplateCopy.businessName = this.getBusinessName(i_serverReply);
        userTemplateCopy.updatedAt = userTemplateCopy.dashboard ? '' : i_serverReply.businessUserInfo.businessInfo.lastLogin;
        userTemplateCopy.createdAt = userTemplateCopy.dashboard ? '' : i_serverReply.businessUserInfo.businessInfo.loginTime;
        userTemplateCopy.payerId = userTemplateCopy.dashboard ? Number(i_serverReply.businessUserInfo?.resellerInfo?.payerId) : Number(i_serverReply.businessUserInfo?.businessInfo?.payerId);
        userTemplateCopy.archiveState = Number(i_serverReply.businessUserInfo?.businessInfo?.archiveState);
        userTemplateCopy.sourceId = Number(i_serverReply.businessUserInfo?.businessInfo?.sourceId);
        userTemplateCopy.fromTemplateId = Number(i_serverReply.businessUserInfo?.businessInfo?.fromTemplateId);
        userTemplateCopy.maxMonitors = Number(i_serverReply.businessUserInfo?.businessInfo?.maxMonitors);
        userTemplateCopy.maxDataStorage = Number(i_serverReply.businessUserInfo?.businessInfo?.maxDataStorage);
        userTemplateCopy.allowSharing = Boolean(i_serverReply.businessUserInfo?.businessInfo?.allowSharing);
        userTemplateCopy.applicationId = userTemplateCopy.dashboard ? '-1' : String(i_serverReply.businessUserInfo.businessInfo.applicationId);
        userTemplateCopy.businessId = userTemplateCopy.dashboard ? userTemplateCopy.businessId = i_serverReply.businessUserInfo.resellerInfo.businessId : i_serverReply.businessUserInfo.businessInfo.businessId;
        userTemplateCopy.resellerId = userTemplateCopy.dashboard ? userTemplateCopy.businessId = i_serverReply.businessUserInfo.resellerInfo.businessId : i_serverReply.businessUserInfo.businessInfo.resellerId;
        userTemplateCopy.enterprise = userTemplateCopy.dashboard ? true : !compareIds(userTemplateCopy.resellerId, '1');
        userTemplateCopy.pricingMethod = i_serverReply.businessUserInfo?.resellerInfo?.pricingMethod;
        userTemplateCopy.localStoreConfigVersion = localStoreConfigVersion;
        userTemplateCopy.accessMask = i_serverReply.businessUserInfo.accessMask;
        userTemplateCopy.privilegeId = i_serverReply.businessUserInfo.privilegeId;
        userTemplateCopy.groupsPrivileges = i_serverReply.businessUserInfo?.privilegeInfo?.Groups ? i_serverReply.businessUserInfo.privilegeInfo.Groups as IGroupsPrivileges : null;
        userTemplateCopy.billingToken = i_serverReply.businessUserInfo?.billingToken;

        return userTemplateCopy;
    }

    public accountCreated(user, pass, serverReply: IUserSync) {
        const userTemplateCopy = this.updateUserSharedFields(serverReply.accountReplyV2, userTemplate);
        userTemplateCopy.studioType = serverReply.accountReplyV2.businessUserInfo.businessInfo.studioType;
        userTemplateCopy.webApiBaseUrl = `${appData.protocol}${serverReply.accountReplyV2.businessUserInfo.businessDomain}`;
        userTemplateCopy.businessDomain = serverReply.accountReplyV2.businessUserInfo.businessDomain;
        userTemplateCopy.username = user;
        userTemplateCopy.email = user;
        userTemplateCopy.passwordNew = pass;
        this.testOverrides(userTemplateCopy);
        this.dispatchUser(userTemplateCopy);
        this.replace(userTemplateCopy);
        this.navigate(userTemplateCopy);
    }

    testOverrides(i_userTemplateCopy) {
        if (Lib.DevMode()) {
            // i_userTemplateCopy.enterprise = true;
            // i_userTemplateCopy.accountStatus = ACCOUNT_STATUS['subscriber'];
            // i_userTemplateCopy.dashboard = true;
            // i_userTemplateCopy.studioType = EnumStudioType.SIGN_STUDIO;
        }
        Lib.DevShow(`Login enterprise[${i_userTemplateCopy.enterprise}] dashboard[${i_userTemplateCopy.dashboard}] studioType[${i_userTemplateCopy.studioType}]`);
    }

    loginSuccess(userSync: IUserSync, request: IAccountRequest, i_serverCallType: serverCallType) {
        const userTemplateCopy = this.updateUserSharedFields(userSync.accountReplyV2, userTemplate);
        const isEnterpriseDashboard = !userSync.accountReplyV2.businessUserInfo.businessInfo;
        userTemplateCopy.studioType = isEnterpriseDashboard ? EnumStudioType.SIGN_STUDIO : userSync.accountReplyV2.businessUserInfo.businessInfo.studioType;
        userTemplateCopy.loginStatus = EnumLoginStatus.LOGIN_PASSED;
        userTemplateCopy.email = request.verifyAccount.userName;
        userTemplateCopy.username = request.verifyAccount.userName;
        userTemplateCopy.passwordNew = request.verifyAccount.password;
        userTemplateCopy.ip = userSync?.ip;
        userTemplateCopy.rememberMe = request.verifyAccount.rememberMe;
        userTemplateCopy.adnetUrls = {
            request: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=customerRequest&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID:&fromChangelistId=0`,
            billing: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=:BILLING_TYPE:&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID:&:DATA:`,
            report: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=:REPORT_TYPE:&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID:&data=:DATA:`,
            save: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=customerSubmit&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID:&data=:DATA:`,
            search: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=search&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID::DATA:`
        };
        userTemplateCopy.webApiResellerBaseUrl = `${appData.protocol}${appData.masterServer}/WebService/ResellerService.ashx?resellerUserName=${userTemplateCopy.email}&resellerPassword=${Lib.Base64().encode(userTemplateCopy.passwordNew)}&base64=1`;
        userTemplateCopy.businessDomain = isEnterpriseDashboard ? '' : userSync.accountReplyV2.businessUserInfo.businessDomain;
        userTemplateCopy.webApiBaseUrl = isEnterpriseDashboard ? '' : `${appData.protocol}${userTemplateCopy.businessDomain}`;
        console.log('Account user version ' + Lib.GetAccountVersion(userTemplateCopy));

        // todo: enable for future support for enterprise two factor when we add preferences support for reseller accounts
        // if (isEnterpriseDashboard) {
        //     this.storeSelector.dispatch(actionPreferencesGet({user: userTemplateCopy, globalService: this.appSettings.globalService, checkTwoFactor: true}));
        // }

        if (userTemplateCopy.enterprise) {
            this.localStorage.setPreloginReseller(userTemplateCopy.resellerId);
        }
        this.testOverrides(userTemplateCopy);

        switch (i_serverCallType) {
            case "VERIFY_V2": {
                this.replace(userTemplateCopy);
                if (request.verifyAccount.rememberMe) {
                } else {
                    this.removeUserFromStorage();
                }
                this.dispatchUser(userTemplateCopy);
                this.navigate(userTemplateCopy);
                break;
            }
            case "STATION_MONITOR": {
                this.replace(userTemplateCopy);
                this.dispatchUser(userTemplateCopy);
                this.router.navigateByUrl('/monitor/poll');
                break;
            }
            case "SUBSCRIBE_STANDALONE": {
                // this.replace(userTemplateCopy);
                this.dispatchUser(userTemplateCopy);
                // this.router.navigateByUrl('/monitor/poll');
                break;
            }
            case "SYNC_USER": {
                this.replace(userTemplateCopy);
                this.dispatchUser(userTemplateCopy);
                this.storeSelector.dispatch(actionUpdateAccountDetails({userDetails: formatAccountDetails(userSync.accountDetails)}));
                break;
            }
        }
    }

    dispatchUser(user) {
        this.storeSelector.logger({message: `user logged in ${userTemplate.email}`, severity: 0});
        this.storeSelector.dispatch(actionSelectedUserUpdate({user}));
    }

    getBusinessName(i_serverReply: IAccountReplyV2): string {
        try {
            if (i_serverReply?.businessUserInfo?.resellerInfo) {
                if (!isNullOrUndefOrEmpty(i_serverReply?.businessUserInfo?.resellerInfo.name)) {
                    return i_serverReply.businessUserInfo.resellerInfo.name;
                }
                if (!isNullOrUndefOrEmpty(i_serverReply?.businessUserInfo?.businessInfo?.name)) {
                    return i_serverReply.businessUserInfo.businessInfo.name;
                }
                const user: IUser = this.localStorage.getItem('IUser', null);
                if (!user || user?.localStoreConfigVersion !== localStoreConfigVersion || user?.username.length < 3 || user?.passwordNew.length < 3) {
                    if (!isNullOrUndefOrEmpty(user.businessName)){
                        return user.businessName;
                    }
                    return 'DigitalSignage';
                }
            } else {
                return 'DigitalSignage';
            }
        } catch (e) {
            return 'DigitalSignage';
        }
    }

    logout(): void {
        this.localStorage.setRemoteLocalStorageConfig();
        this.removeUserFromStorage();
        this.removeSettingsFromStorage();
        this.removeAppCache();
        this.socialSignOut();
        const user: IUser = this.getEmptyUser();
        user.loginStatus = EnumLoginStatus.LOGOUT;
        this.user$.next(user);
    }

    loginFailed() {
        this.removeUserFromStorage();
        this.socialSignOut();
        const user: IUser = this.getEmptyUser();
        user.loginStatus = EnumLoginStatus.LOGIN_FAILED;
        this.user$.next(user);
        this.localStorage.setStorageValConfig('accountInitialCopyResources', '');
        this.toastr.error('login failed, please contact live chat');
        this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.LOGIN}));
    }

    replace(model: IUser): void {
        if (this.loadLocalStorage) {
            this.setUserIntoStorage(model);
            this.user$.next(model);
        }
    }

    rewind(): void {
        if (this.loadLocalStorage) {
            this.user$.next(this.getUserFromStorage());
        }
    }

    navigate(i_user: IUser): void {
        if (this.currentUrl && this.currentUrl.toLowerCase().indexOf('checkout') > -1) {
            this.router.navigateByUrl('/shopping-cart/checkout');
            return;
        }
        if (this.currentUrl && this.currentUrl.toLowerCase().indexOf('shopping') > -1) {
            return;
        }
        switch (i_user.studioType) {
            case EnumStudioType.LITE: {

                /**
                 disabled 3-17-2024, now will fall through same as [ case EnumStudioType.PRO ]:
                 this.toastr.error(this.translocoService.translate('toast.redirectLite', {}, this.translocoService.getActiveLang()));
                 this.logout();
                 window.location.replace(this.appSettings.studioLiteLegacy);
                 this.logout();
                 **/

                this.router.navigateByUrl('/getstarted/downloads/studio');
                break;

                // setTimeout(() => {
                //   this.toastr.info(this.translocoService.translate('toast.loggedLegacy', {}, this.translocoService.getActiveLang()));
                // }, 100);
                // setTimeout(() => {
                //   this.toastr.warning(this.translocoService.translate('toast.redirectLite', {}, this.translocoService.getActiveLang()));
                // }, 2000);
                // setTimeout(() => {
                //   Lib.AlertOnLeaveOff();
                //   window.location.replace(this.appSettings.studioLiteLegacy);
                // }, 5000);
            }
            case EnumStudioType.PRO: {
                // this.logout();
                this.router.navigateByUrl('/getstarted/downloads/studio');
                break;
            }
            case EnumStudioType.SIGN_STUDIO: {
                this.toastr.clear();
                this.navigateOverview();
                // this.router.navigateByUrl(this.redirectUrl || '/');
                break;
            }
        }
    }

    public navigateOverview(): void {
        this.router.navigateByUrl('/home');
        // this.cod(
        //     combineLatest([
        //         this.accessService.getGroupsCollection("SignStudio Editor"),
        //         this.accessService.getGroupsCollection("SignStudio Dashboard"),
        //         this.accessService.getGroupsCollection("SignStudio Players")])
        //         .pipe(take(1))
        //         .subscribe(([signStudioEditor, signStudioDashboard, signStudioPlayers]) => {
        //                 /** didn't find the user assigned privileges group so must have been recently added XML, just route to overview **/
        //                 if (isNullOrUndef(signStudioEditor) || isNullOrUndef(signStudioDashboard) || isNullOrUndef(signStudioPlayers)) {
        //                     return this.router.navigateByUrl('/home');
        //                 }
        //                 const enableDashboard = this.localStorage.getStorageValConfig('enableDashboard');
        //                 /** dashboard allowed **/
        //                 if (signStudioDashboard.$.visible === '1' && enableDashboard === '1') {
        //                     return this.router.navigateByUrl('/home');
        //                 }
        //                 /** if editor allowed go there **/
        //                 if (signStudioEditor.$.visible === '1') {
        //                     return this.router.navigateByUrl('editor/list');
        //                 }
        //                 /** if players allowed go there **/
        //                 if (signStudioPlayers.$.visible === '1') {
        //                     return this.router.navigateByUrl('stations');
        //                 }
        //             }
        //         )
        // );
    }

    public backToLogin() {
        this.router.navigateByUrl('/login/' + _.uniqueId());
        setTimeout(() => {
            this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.LOGIN}));
        }, 10);
    }

    public createUserSocial(i_socialServerReply: ISocialServerReply | IAmazonLogin) {
        const templateId = this.localStorage.getStorageValConfig('socialUserPendingTemplateId');
        if (i_socialServerReply.exists) return;
        if (i_socialServerReply.profile) {
            let userName;
            let password = null;
            if (i_socialServerReply.profile?.access) {
                userName = i_socialServerReply.socialUser.email;
            } else if (i_socialServerReply.profile['ldap']) {
                userName = i_socialServerReply.msUser;
                password = i_socialServerReply.msPassword;
            } else if (i_socialServerReply.profile['iss']) {
                userName = i_socialServerReply.profile['email'];
            } else {
                userName = i_socialServerReply.profile['emails'][0].value;
            }
            this.toastr.info('creating account...');
            this.createUser('', userName, password, userName, templateId, this.resellerId, EnumStudioType.SIGN_STUDIO);
        } else {
            this.toastr.error(this.translocoService.translate('toast.probSocialRep', {}, this.translocoService.getActiveLang()));
        }
    }

    public createUser(businessName: string, userName: string, password: string, contactEmail: string, templateSampleId: string, resellerId: string, studioType: EnumStudioType) {
        if (isNullOrUndefOrEmpty(password)) password = Lib.RandomString();
        if (isNullOrUndefOrEmpty(businessName)) businessName = 'DigitalSignage';
        this.storeSelector.appStore.dispatch(actionAccountCommandsRequest({
                accountRequest: {
                    createCustomerAccount: {
                        businessName,
                        userName,
                        password,
                        resellerId,
                        studioType,
                        templateBusinessId: templateSampleId,
                        firstName: '',
                        lastName: '',
                        contactEmail,
                        workPhone: '',
                        cellPhone: '',
                        address: '',
                        city: '',
                        state: '',
                        contry: '',
                        zipcode: '',
                        emailUpdates: '',
                        callback: ''
                    },
                    createAccount: {
                        templateId: templateSampleId
                    },
                    serverCall: "CREATE"
                }
            })
        );
    }

    public getEmptyUser(): IUser {
        return {
            loginStatus: null,
            businessId: null,
            ip: null,
            businessName: null,
            accountVersion: null,
            pricingMethod: null,
            customStudio: null,
            payerId: null,
            webApiBaseUrl: null,
            webApiResellerBaseUrl: null,
            adnetUrls: null,
            username: null,
            avatar: null,
            avatarURL: null,
            email: null,
            status: null,
            description: null,
            statusLabel: null,
            isEnabled: null,
            studioType: null,
            createdAt: null,
            updatedAt: null,
            passwordNew: null,
            rememberMe: null,
            accessToken: null,
            name: null,
            cardNumber: null,
            cardType: null,
            businessDomain: null,
            resellerId: null,
            enterprise: null,
            dashboard: null,
            plus: null,
            applicationId: null,
            mediaCloud: null,
            localStoreConfigVersion: null,
            accessMask: null,
            accountStatus: null,
            accountStatusReseller: null,
            webToken: null,
            archiveState: null,
            sourceId: null,
            fromTemplateId: null,
            maxMonitors: null,
            maxDataStorage: null,
            allowSharing: null,
            privilegeId: null,
            groupsPrivileges: null
        };
    }
}

